import pushApi from '../api/pushApi';

const SERVICE_WORKER_PATH = '/service-worker.js';

/**
 * Convert VAPID key from base64url to Uint8Array
 */
export function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

/**
 * Get browser language
 */
export function getLanguage() {
    const lang = navigator.language || navigator.userLanguage;
    if (lang) {
        const parts = lang.toLowerCase().split('-');
        return parts[0].substring(0, 2);
    }
    return 'vi';
}

/**
 * Get timezone offset in minutes
 */
export function getTimezoneOffset() {
    return -60 * new Date().getTimezoneOffset();
}

/**
 * Check if push notifications are supported
 */
export function isPushNotificationSupported() {
    return (
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'showNotification' in ServiceWorkerRegistration.prototype
    );
}

/**
 * Check notification permission
 */
export function checkNotificationPermission() {
    if (!('Notification' in window)) {
        return 'unsupported';
    }
    return Notification.permission; // 'default', 'granted', or 'denied'
}

/**
 * Request notification permission
 */
export function askPermission() {
    return new Promise((resolve, reject) => {
        if (!('Notification' in window)) {
            reject(new Error('Notifications are not supported'));
            return;
        }

        const permissionResult = Notification.requestPermission((result) => {
            resolve(result);
        });

        if (permissionResult) {
            permissionResult.then(resolve, reject);
        }
    });
}

/**
 * Register service worker
 */
export async function registerServiceWorker() {
    if (!('serviceWorker' in navigator)) {
        throw new Error('Service workers are not supported');
    }

    try {
        const registration = await navigator.serviceWorker.register(SERVICE_WORKER_PATH, {
            updateViaCache: 'none',
        });
        console.log('[SW] Service worker registered:', registration);
        return registration;
    } catch (error) {
        console.error('[SW] Service worker registration failed:', error);
        throw error;
    }
}

/**
 * Get VAPID public key from server
 */
export async function getVapidPublicKey() {
    try {
        const response = await pushApi.getVapidPublicKey();
        return response.data.data.publicKey;
    } catch (error) {
        console.error('Failed to get VAPID public key:', error);
        throw error;
    }
}

/**
 * Subscribe to push notifications
 */
export async function subscribeToPush(vapidPublicKey) {
    if (!isPushNotificationSupported()) {
        throw new Error('Push notifications are not supported');
    }

    const permission = checkNotificationPermission();
    if (permission !== 'granted') {
        throw new Error('Notification permission not granted');
    }

    try {
        const registration = await navigator.serviceWorker.ready;
        const subscribeOptions = {
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
        };

        const subscription = await registration.pushManager.subscribe(subscribeOptions);
        return subscription;
    } catch (error) {
        console.error('Failed to subscribe to push:', error);
        throw error;
    }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPush() {
    if (!('serviceWorker' in navigator)) {
        throw new Error('Service workers are not supported');
    }

    try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();

        if (subscription) {
            await subscription.unsubscribe();
            return true;
        }
        return false;
    } catch (error) {
        console.error('Failed to unsubscribe from push:', error);
        throw error;
    }
}

/**
 * Get current push subscription
 */
export async function getSubscription() {
    if (!('serviceWorker' in navigator)) {
        return null;
    }

    try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        return subscription;
    } catch (error) {
        console.error('Failed to get subscription:', error);
        return null;
    }
}

/**
 * Convert subscription to object for sending to server
 */
export function subscriptionToObject(subscription) {
    const key = subscription.getKey('p256dh');
    const token = subscription.getKey('auth');
    const encoding = (window.PushManager?.supportedContentEncodings || ['aesgcm'])[0];

    return {
        endpoint: subscription.endpoint,
        key: key ? btoa(String.fromCharCode.apply(null, new Uint8Array(key))) : null,
        token: token ? btoa(String.fromCharCode.apply(null, new Uint8Array(token))) : null,
        encoding,
        language: getLanguage(),
        timezone: getTimezoneOffset(),
        devicemodel: navigator.platform || 'Unknown',
        useragent: navigator.userAgent || '',
        location: window.location.href,
    };
}

/**
 * Send subscription to server
 */
export async function sendSubscriptionToServer(subscription, method = 'POST') {
    const subscriptionData = subscriptionToObject(subscription);

    try {
        if (method === 'POST') {
            await pushApi.subscribePush(subscriptionData);
        } else if (method === 'PUT') {
            await pushApi.updatePushSubscription(subscriptionData);
        }
        return true;
    } catch (error) {
        console.error('Failed to send subscription to server:', error);
        throw error;
    }
}

/**
 * Remove subscription from server
 */
export async function removeSubscriptionFromServer(subscription) {
    const subscriptionData = {
        endpoint: subscription.endpoint,
    };

    try {
        await pushApi.unsubscribePush(subscriptionData);
        return true;
    } catch (error) {
        console.error('Failed to remove subscription from server:', error);
        throw error;
    }
}

/**
 * Update subscription on server
 */
export async function updateSubscriptionOnServer(subscription) {
    return sendSubscriptionToServer(subscription, 'PUT');
}

